import React, { useState } from "react";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import Divider from "@material-ui/core/Divider";
import { FulfillingBouncingCircleSpinner } from "react-epic-spinners";

/**
 * Componente para mostrar el resumen de compra
 * @param {number} subtotal - Subtotal de productos
 * @param {number} envio - Costo de envío
 * @param {number} total - Total a pagar
 * @param {number} totalProductos - Total de productos (cantidad)
 * @param {Array} mensajesValidacion - Mensajes de validación
 * @param {boolean} isValid - Si el formulario es válido
 * @param {Function} onConfirm - Callback para confirmar compra
 * @param {Function} formatPrice - Función para formatear el precio
 * @param {boolean} isProcessing - Si está procesando la compra
 * @param {string} buttonText - Texto del botón
 * @param {boolean} mostrarEnvio - Si se debe mostrar la línea de envío
 * @param {number} saldoAplicado - Saldo aplicado
 * @param {number} descuentoCupon - Descuento del cupón aplicado
 * @param {object} cupon - Información del cupón aplicado
 * @param {Function} onAplicarCupon - Callback para aplicar cupón
 * @param {Function} onRemoverCupon - Callback para remover cupón
 * @param {string} idUsuario - ID del usuario
 */
const PurchaseSummary = ({
	subtotal,
	envio,
	total,
	totalProductos,
	mensajesValidacion,
	isValid,
	onConfirm,
	formatPrice,
	isProcessing,
	buttonText = "Continuar",
	mostrarEnvio = true,
	saldoAplicado = 0,
	descuentoCupon = 0,
	cupon = null,
	onAplicarCupon,
	onRemoverCupon,
	idUsuario,
}) => {
	const [codigoCupon, setCodigoCupon] = useState("");
	const [aplicandoCupon, setAplicandoCupon] = useState(false);
	const [errorCupon, setErrorCupon] = useState("");

	const handleAplicarCupon = async () => {
		if (!codigoCupon.trim()) {
			setErrorCupon("Ingresa un código de cupón");
			return;
		}

		if (!idUsuario) {
			setErrorCupon("Debes iniciar sesión para usar cupones");
			return;
		}

		setAplicandoCupon(true);
		setErrorCupon("");

		try {
			if (onAplicarCupon) {
				await onAplicarCupon(codigoCupon.trim().toUpperCase(), subtotal, totalProductos, envio);
				setCodigoCupon("");
			}
		} catch (error) {
			setErrorCupon(error.message || "Error al aplicar el cupón");
		} finally {
			setAplicandoCupon(false);
		}
	};

	const handleRemoverCupon = () => {
		if (onRemoverCupon) {
			onRemoverCupon();
		}
		setCodigoCupon("");
		setErrorCupon("");
	};

	return (
		<Card className="carrito-card">
			<CardBody className="carrito-card-body">
				<div className="carrito-card-title-section">
					<h4 className="carrito-card-title">Resumen de compra</h4>
					<Divider />
				</div>
				{/* Sección de cupón */}
				{!cupon?.aplicado ? (
					<div style={{ marginBottom: "20px", padding: "15px 0" }}>
						<div style={{ display: "flex", gap: "10px", marginBottom: "10px" }}>
							<CustomInput
								labelText="Código de cupón"
								formControlProps={{
									fullWidth: true,
								}}
								inputProps={{
									value: codigoCupon,
									onChange: (e) => {
										setCodigoCupon(e.target.value);
										setErrorCupon("");
									},
									placeholder: "Ej: BIENVENIDA20",
									disabled: aplicandoCupon,
									onKeyPress: (e) => {
										if (e.key === "Enter") {
											handleAplicarCupon();
										}
									},
								}}
							/>
							<Button
								color="default"
								size="sm"
								onClick={handleAplicarCupon}
								disabled={aplicandoCupon || !codigoCupon.trim()}
								style={{ 
									marginTop: "20px", 
									minWidth: "100px",
									border: "1px solid #ccc",
									backgroundColor: "#fafafa",
									color: "#333",
									boxShadow: "none"
								}}
							>
								{aplicandoCupon ? "Aplicando..." : "Aplicar"}
							</Button>
						</div>
						{errorCupon && (
							<div style={{ 
								color: errorCupon.includes("envío gratis") ? "#2196f3" : "#f44336", 
								fontSize: "12px", 
								marginTop: "5px",
								padding: "8px",
								backgroundColor: errorCupon.includes("envío gratis") ? "#e3f2fd" : "#ffebee",
								borderRadius: "4px",
								border: `1px solid ${errorCupon.includes("envío gratis") ? "#2196f3" : "#f44336"}`
							}}>
								{errorCupon}
							</div>
						)}
					</div>
				) : (
					<div style={{ 
						marginBottom: "20px", 
						padding: "15px", 
						backgroundColor: "#e8f5e9", 
						borderRadius: "8px", 
						border: "1px solid #4caf50" 
					}}>
						<div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
							<div>
								<div style={{ fontWeight: "bold", color: "#2e7d32", marginBottom: "5px" }}>
									Cupón aplicado: {cupon.codigo}
								</div>
								<div style={{ fontSize: "12px", color: "#666" }}>
									Descuento: {formatPrice(descuentoCupon)}
								</div>
							</div>
							<Button
								color="default"
								size="sm"
								onClick={handleRemoverCupon}
								style={{ 
									minWidth: "80px",
									border: "1px solid #ccc",
									backgroundColor: "#fafafa",
									color: "#333",
									boxShadow: "none"
								}}
							>
								Remover
							</Button>
						</div>
					</div>
				)}
				<div className="carrito-resumen">
					<div className="carrito-resumen-item">
						<span className="resumen-label">Productos ({totalProductos}):</span>
						<span className="resumen-value">{formatPrice(subtotal)}</span>
					</div>
					{descuentoCupon > 0 && (
						<div className="carrito-resumen-item" style={{ color: "#4caf50" }}>
							<span className="resumen-label">Descuento:</span>
							<span className="resumen-value">-{formatPrice(descuentoCupon)}</span>
						</div>
					)}
					{mostrarEnvio && (
						<div className="carrito-resumen-item">
							<span className="resumen-label">Envío:</span>
							<span className="resumen-value">{formatPrice(envio)}</span>
						</div>
					)}
					{saldoAplicado > 0 && (
						<div className="carrito-resumen-item">
							<span className="resumen-label">Saldo aplicado:</span>
							<span className="resumen-value">{formatPrice(saldoAplicado)}</span>
						</div>
					)}
					<div className="carrito-resumen-total">
						<span className="total-label">Total:</span>
						<span className="total-value">{formatPrice(total)}</span>
					</div>
					{/* Mensajes de validación */}
					{mensajesValidacion.length > 0 && (
						<div className="carrito-mensajes-validacion">
							{mensajesValidacion.map((mensaje, index) => (
								<div key={index} className="mensaje-validacion">
									{mensaje}
								</div>
							))}
						</div>
					)}
					<Button
						disabled={!isValid}
						onClick={onConfirm}
						size="lg"
						fullWidth
						className="carrito-button-primary"
					>
						{buttonText}
						{isProcessing && (
							<FulfillingBouncingCircleSpinner
								size={20}
								color="black"
								style={{ marginLeft: "8px" }}
							/>
						)}
					</Button>
				</div>
			</CardBody>
		</Card>
	);
};

export default PurchaseSummary;
